/**
 * Composer 행 그리드 사진 선언 실행 훅 — 템플릿 선언(`DataSourceFieldMap.photoAnalysis`/`syncTo`)
 * 을 소비하는 **범용 배선**이다(도메인 어휘 0 — 소스 키·필드명은 전부 선언이 소유, 규약 정본은
 * shared `RowPhotoAnalysisConfig`/`RowFieldSyncConfig` JSDoc / 순수 로직은
 * `lib/composerPhotoAnalysis`). 결정 2026-09-07 추기 4(사진 UI의 잠재위험요인 섹션 흡수).
 *
 * 흐름:
 * 1. 호스트 handleChange가 `onFieldChange(key, next, prev)`를 호출(호스트 머지 **뒤** — 앞이면
 *    함수형 갱신 큐에서 이 훅의 행 상태 쓰기가 키 통째 머지에 덮인다).
 * 2. 사진 규칙 소스: 업로드 전이 감지 → 파생 행 upsert(deriveTo — 본문 사진 카드 반영) →
 *    행별 「AI 분석」 체크(enabledField) 확인 → 그 행에 기존 값이 있으면 덮어쓰기 확인
 *    (`confirmOpen` — 호스트가 ConfirmModal 렌더), 없으면 즉시 분석.
 *    분석 성공: 첫 결과가 그 행 fills를 채우고, 나머지는 rowLimit 여유 내 새 행(spill —
 *    초과분은 토스트 안내). 진행 표시는 행 필드 `PHOTO_ANALYSIS_STATUS_FIELD`(종료 시 제거).
 *    이어서 파생 행 투영(projectDerivedRows)이 사진 카드 자식 항목을 최신 입력으로 맞춘다 —
 *    투영은 이후의 수기 편집·행 삭제·사진 삭제에도 계속 단방향으로 따라간다(파생 행은 사용자
 *    편집 채널이 아니라 훅 소유 projection).
 * 3. 체크 전파 규칙 소스: 체크 변경 행을 감지해 짝 행(viaField↔targetViaField 동등)과 형제
 *    행(mirrorSiblings)에 값을 전파한다(그리기 메모 토글의 본문 판정 반영).
 *
 * 분석 API는 기존 사진 위험요인 분석 엔드포인트(analyzePhotoForTBM — 요청은 photoUrl+companyId뿐,
 * 호스트의 단일 사진 분석 능력)를 재사용한다.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { ReportTemplate } from '@safetyhub/shared';
import { PHOTO_ANALYSIS_STATUS_FIELD } from '@safetyhub/shared';
import { analyzePhotoForTBM } from '../lib/tbm';
import {
  applyRowSync,
  buildSpillRows,
  collectPhotoAnalysisRules,
  collectRowSyncRules,
  detectCheckboxChanges,
  detectPhotoUploads,
  findingFillPatch,
  hasExistingRowContent,
  isAnalysisEnabled,
  projectDerivedRows,
  stripPhotoAnalysisTransientFields,
  updateSourceRow,
  upsertDerivedPhotoRow,
  type PhotoAnalysisFinding,
  type PhotoAnalysisRule,
} from '../lib/composerPhotoAnalysis';

const ANALYZING_TEXT = 'AI 분석 중...';

interface UseComposerPhotoAnalysisArgs {
  template: ReportTemplate | null;
  /** 현재 렌더의 content 값 — 분석 완료 시점 판정의 읽기 스냅샷(ref로 최신 유지). */
  values: Record<string, unknown>;
  setValues: Dispatch<SetStateAction<Record<string, unknown>>>;
  /** 미지정(빈 값)이면 전체 미배선 — 분석 없이 degrade(업로드·전파 자체는 동작). */
  companyId: string;
  showToast: (message: string, type?: 'error' | 'success' | 'info' | 'warning') => void;
}

export interface ComposerPhotoAnalysisHandle {
  /** 호스트 handleChange에서 호출 — prevValues는 갱신 전 스냅샷(현재 렌더의 values). */
  onFieldChange: (
    bindingKey: string,
    nextValue: unknown,
    prevValues: Record<string, unknown>,
  ) => void;
  /** 덮어쓰기 확인 모달 열림 여부(호스트가 ConfirmModal 렌더). */
  confirmOpen: boolean;
  confirmOverwrite: () => void;
  cancelOverwrite: () => void;
  /**
   * 저장 payload 정화 — 분석 진행 상태 행 필드(비예약 UI 상태 키)를 걷어 낸다. 정상 흐름은
   * 종료 시 키가 제거되지만, 분석 중 저장이 일어나도 content에 잔존하지 않게 하는 방어선.
   * 규칙 없는 템플릿·잔존 없음이면 원본 참조 그대로다.
   */
  sanitizeContent: (values: Record<string, unknown>) => Record<string, unknown>;
}

export function useComposerPhotoAnalysis({
  template,
  values,
  setValues,
  companyId,
  showToast,
}: UseComposerPhotoAnalysisArgs): ComposerPhotoAnalysisHandle {
  const rules = useMemo(
    () => collectPhotoAnalysisRules(template?.dataSources),
    [template],
  );
  const syncRules = useMemo(() => collectRowSyncRules(template?.dataSources), [template]);
  const dataSources = template?.dataSources;

  // 최신 값 읽기 채널 — 비동기 완료 시점의 판정(행 생존·spill 여유)이 렌더 클로저의 낡은
  // 스냅샷이 아니라 현재 행을 보게 한다. 쓰기는 언제나 setValues 함수형 갱신(순수 updater)이다.
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // 같은 행·같은 URL 재발화 가드 — 한 이벤트의 다중 onChange(행 스코프 pending 누적)나
  // 후속 무관 편집(같은 배열이 다시 흘러옴)이 분석을 중복 트리거하지 않게 한다.
  const triggeredRef = useRef(new Map<string, string>());
  // spill 1회 가드(행 단위) — 재분석(사진 교체)은 그 행 fills만 덮고 새 행을 다시 늘리지 않는다.
  const spilledRef = useRef(new Set<string>());
  const [confirmRequest, setConfirmRequest] = useState<{
    rule: PhotoAnalysisRule;
    rowId: string;
    url: string;
  } | null>(null);

  const runAnalysis = useCallback(
    async (rule: PhotoAnalysisRule, rowId: string, url: string): Promise<void> => {
      const cfg = rule.config;
      setValues((prev) =>
        updateSourceRow(prev, rule.sourceKey, rowId, (row) => ({
          ...row,
          [PHOTO_ANALYSIS_STATUS_FIELD]: ANALYZING_TEXT,
        })),
      );
      try {
        const result = await analyzePhotoForTBM(url, companyId);
        const findings: PhotoAnalysisFinding[] = (result.findings ?? []).map((f) => ({
          description: f.description,
          measures: f.measures ?? [],
        }));
        // 판정·spill 계산·가드 갱신은 전부 **updater 밖**에서 — React updater는 순수해야 한다
        // (StrictMode 이중 실행에서 ref 가드가 updater 안에 있으면 2회차가 spill을 건너뛰어
        // 최종 상태에서 사라진다). 최신 행은 valuesRef로 읽는다.
        const current = valuesRef.current;
        const currentRows = Array.isArray(current[rule.sourceKey])
          ? (current[rule.sourceKey] as Record<string, unknown>[])
          : [];
        const rowAlive = currentRows.some((r) => r.rowId === rowId);
        const fillPatch =
          rowAlive && findings.length > 0 ? findingFillPatch(findings[0], cfg.fills) : undefined;
        let spillRows: Record<string, unknown>[] = [];
        let limitDropped = 0;
        if (
          cfg.spillNewRows &&
          rowAlive &&
          findings.length > 1 &&
          !spilledRef.current.has(rowId)
        ) {
          spilledRef.current.add(rowId);
          const targetDef = dataSources?.find((d) => d.key === rule.sourceKey);
          ({ rows: spillRows, limitDropped } = buildSpillRows(
            findings.slice(1),
            url,
            rule,
            targetDef,
            currentRows.length,
          ));
        }
        setValues((prev) => {
          // 상태 키 제거 + (결과 있으면) fills 채움 — 0건이면 기존 입력 유지(구 동형).
          // updateSourceRow가 행 부재(분석 중 행 삭제)면 prev 그대로 = stale 커밋 폐기.
          let next = updateSourceRow(prev, rule.sourceKey, rowId, (row) => {
            const updated = { ...row };
            delete updated[PHOTO_ANALYSIS_STATUS_FIELD];
            if (fillPatch) Object.assign(updated, fillPatch);
            return updated;
          });
          if (spillRows.length > 0 && next !== prev) {
            const cur = Array.isArray(next[rule.sourceKey])
              ? (next[rule.sourceKey] as Record<string, unknown>[])
              : [];
            next = { ...next, [rule.sourceKey]: [...cur, ...spillRows] };
          }
          // 파생 사진 카드 투영 — 채워진 fills·spill 행이 본문 사진 카드 자식 항목에 실린다.
          return projectDerivedRows(next, rule);
        });
        if (limitDropped > 0) {
          showToast(
            `위험요인 표가 가득 차 ${limitDropped}건은 표에 담지 못했습니다.`,
            'info',
          );
        }
      } catch {
        // 실패 — 진행 상태만 복원(구 동형: 값 불변) + 안내.
        setValues((prev) =>
          updateSourceRow(prev, rule.sourceKey, rowId, (row) => {
            const updated = { ...row };
            delete updated[PHOTO_ANALYSIS_STATUS_FIELD];
            return updated;
          }),
        );
        showToast('AI 분석에 실패했습니다. 위험요인을 직접 입력해주세요.', 'error');
      }
    },
    [companyId, dataSources, setValues, showToast],
  );

  const onFieldChange = useCallback<ComposerPhotoAnalysisHandle['onFieldChange']>(
    (bindingKey, nextValue, prevValues) => {
      // 사진 규칙 — 업로드 전이·삭제/편집 투영.
      for (const rule of rules) {
        if (rule.sourceKey !== bindingKey) continue;
        // 사진 삭제·행 삭제 시 재발화 가드 해제 — 같은 URL을 다시 올려도 새 업로드로 취급한다.
        const nextRows = Array.isArray(nextValue)
          ? (nextValue as Record<string, unknown>[])
          : [];
        for (const [rowId, url] of [...triggeredRef.current]) {
          const row = nextRows.find((r) => r.rowId === rowId);
          const cur = typeof row?.[rule.urlField] === 'string' ? (row[rule.urlField] as string).trim() : '';
          if (!row || cur !== url) triggeredRef.current.delete(rowId);
        }
        const uploads = detectPhotoUploads(prevValues[bindingKey], nextValue, rule.urlField);
        for (const up of uploads) {
          if (triggeredRef.current.get(up.rowId) === up.url) continue;
          triggeredRef.current.set(up.rowId, up.url);
          // 파생 행 upsert(업로드 즉시 — 본문 사진 카드에 사진이 실린다. AI 여부와 무관).
          if (rule.config.deriveTo) {
            const targetDef = dataSources?.find(
              (d) => d.key === rule.config.deriveTo?.sourceKey,
            );
            setValues((prev) =>
              projectDerivedRows(
                upsertDerivedPhotoRow(prev, rule, targetDef, up.rowId, up.url),
                rule,
              ),
            );
          }
          if (!companyId) continue; // 분석 미배선 — 업로드·파생만
          if (!isAnalysisEnabled(up.row, rule.config.enabledField)) continue;
          if (hasExistingRowContent(up.row, rule.config.fills)) {
            setConfirmRequest({ rule, rowId: up.rowId, url: up.url });
          } else {
            void runAnalysis(rule, up.rowId, up.url);
          }
        }
        // 투영 — 행 삭제·사진 삭제(파생 정리)와 fills 수기 편집(사진 카드 자식 갱신)을
        // 한 번에 따라간다(변경 없으면 원본 참조 그대로라 무해).
        if (rule.config.deriveTo) {
          setValues((prev) => projectDerivedRows(prev, rule));
        }
      }
      // 체크 전파 규칙 — 짝 행 + 형제 행 반영.
      for (const rule of syncRules) {
        if (rule.sourceKey !== bindingKey) continue;
        const changes = detectCheckboxChanges(prevValues[bindingKey], nextValue, rule.field);
        for (const change of changes) {
          setValues((prev) => applyRowSync(prev, rule, change));
        }
      }
    },
    [rules, syncRules, companyId, dataSources, runAnalysis, setValues],
  );

  const confirmOverwrite = useCallback(() => {
    // 부수효과는 updater 밖에서 — StrictMode의 updater 이중 호출이 분석을 두 번 돌리지 않게.
    if (!confirmRequest) return;
    setConfirmRequest(null);
    void runAnalysis(confirmRequest.rule, confirmRequest.rowId, confirmRequest.url);
  }, [confirmRequest, runAnalysis]);

  const cancelOverwrite = useCallback(() => setConfirmRequest(null), []);

  const sanitizeContent = useCallback(
    (content: Record<string, unknown>) =>
      stripPhotoAnalysisTransientFields(content, rules, PHOTO_ANALYSIS_STATUS_FIELD),
    [rules],
  );

  return {
    onFieldChange,
    confirmOpen: confirmRequest !== null,
    confirmOverwrite,
    cancelOverwrite,
    sanitizeContent,
  };
}
